import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BrowserShot } from './BrowserShot';
import { LiveSnakWindow } from './LiveSnakWindow';
import { screenshots } from '../../data/deck';
interface WebsiteSnakDemoProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The existing Filtek website with the compact invitation. Clicking the
 * invitation expands the Snak window on top of the page.
 * Positions are percentages of the cropped screenshot.
 */
export function WebsiteSnakDemo({
  open,
  onOpenChange
}: WebsiteSnakDemoProps) {
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    if (!open || playing) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, playing, onOpenChange]);
  return <BrowserShot src={screenshots.websiteInvite} alt="Existing Filtek website with a compact Meet the Filteks invitation card in the lower left corner" data-id="element-466" data-mp="l6zzp0wcr0n6">
      {/* invitation hotspot */}
      {!open && <button type="button" onClick={() => onOpenChange(true)} aria-label="Open the Meet the Filteks experience" className="group absolute rounded-[14px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" style={{
      left: '1.1%',
      top: '68.6%',
      width: '20.4%',
      height: '30.6%'
    }} data-id="element-467" data-mp="26gu9pwqyrhj3">
          <motion.span aria-hidden="true" className="absolute inset-0 rounded-[14px] ring-2 ring-primary" animate={{
        opacity: [0.9, 0.2, 0.9],
        scale: [1, 1.03, 1]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} data-id="element-468" data-mp="1sfhytzqnu642" />
          <span className="absolute -top-9 left-0 whitespace-nowrap rounded-full bg-background px-3 py-1.5 font-body text-[10px] uppercase tracking-[0.16em] text-primary shadow-lg transition-transform group-hover:-translate-y-0.5" data-id="element-469" data-mp="3hupov5hvbqab">
            Click to meet the family
          </span>
        </button>}

      <AnimatePresence data-id="element-470" data-mp="1fzrzd6re9iza">
        {open && <>
            <motion.button key="dim" type="button" aria-label="Close the experience" onClick={() => onOpenChange(false)} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.35
        }} className="absolute inset-0 cursor-default bg-[rgba(11,21,18,0.45)] focus:outline-none" data-id="element-471" data-mp="127vbjewrdv8p" />
            <motion.div key="snak" role="dialog" aria-label="Meet the Filteks Snak opened above the website" initial={{
          opacity: 0,
          scale: 0.28
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.28
        }} transition={{
          duration: 0.55,
          ease: [0.22, 1, 0.36, 1]
        }} className="absolute overflow-hidden rounded-[2.2%] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.6)]" style={{
          left: '14.7%',
          top: '4.2%',
          width: '70.8%',
          height: '86.9%',
          transformOrigin: '-6% 105%'
        }} data-id="element-472" data-mp="1kzo3d603daeu">
              <img src={screenshots.websiteSnak} alt="Meet the Filteks Snak showing the family portrait: Firstborn, Middle child, Youngest and Caboose child" className="absolute block max-w-none" style={{
            width: '141.24%',
            left: '-20.72%',
            top: 0,
            marginTop: '-9.76%'
          }} data-id="element-473" data-mp="axr4rinajn53" />
              <button type="button" onClick={() => setPlaying(true)} aria-label="Introduce me — start the Snak" className="group absolute rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white" style={{
            left: '35.5%',
            top: '85.6%',
            width: '28.9%',
            height: '8.4%'
          }} data-id="element-474" data-mp="3odf8nr26v62g">
                <motion.span aria-hidden="true" className="absolute inset-0 rounded-full ring-2 ring-background" animate={{
              opacity: [0.7, 0, 0.7],
              scale: [1, 1.08, 1]
            }} transition={{
              repeat: Infinity,
              duration: 2,
              ease: 'easeInOut'
            }} data-id="element-475" data-mp="23wplx1ja8p7l" />
              </button>
              <button type="button" onClick={() => onOpenChange(false)} aria-label="Close" className="absolute rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" style={{
            left: '91%',
            top: '3.5%',
            width: '8%',
            height: '6%'
          }} data-id="element-476" data-mp="2hr03mywv3j45" />
            </motion.div>
          </>}
      </AnimatePresence>

      <AnimatePresence data-id="element-477" data-mp="11anr7qb12bb8">
        {playing && <LiveSnakWindow key="live" onClose={() => setPlaying(false)} data-id="element-478" data-mp="11bp29us1mxsy" />}
      </AnimatePresence>
    </BrowserShot>;
}